import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import AFIPVerification from '../components/AFIPVerification';
import {
  Building2,
  ArrowLeft,
  Eye,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Send,
  MessageSquare,
  FileText,
  DollarSign,
  Mail,
  Key,
  User,
  Users,
  Calendar,
  MapPin,
  Phone,
  Edit2,
  Trash2,
  Download,
  ExternalLink,
  UserPlus,
  Plus,
  Forward,
  History,
  CreditCard,
  X,
  Copy,
  Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';

// Estados del onboarding
const ESTADOS = {
  PENDIENTE: { label: 'Pendiente', color: 'bg-slate-100 text-slate-700', icon: Clock },
  EN_PROGRESO: { label: 'En Progreso', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  ENVIADO: { label: 'Enviado', color: 'bg-purple-100 text-purple-700', icon: Send },
  EN_REVISION: { label: 'En Revisión', color: 'bg-amber-100 text-amber-700', icon: Eye },
  INFO_ADICIONAL: { label: 'Info Adicional', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  NIVEL_1_APROBADO: { label: 'Aprobado Nivel 1', color: 'bg-cyan-100 text-cyan-700', icon: CheckCircle },
  NIVEL_1_DEVUELTO: { label: 'Devuelto por Nivel 2', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  APROBADO: { label: 'Aprobado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  RECHAZADO: { label: 'Rechazado', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const formatCurrency = (value) => {
  if (!value) return '$0';
  return new Intl.NumberFormat('es-AR', { 
    style: 'currency', 
    currency: 'ARS', 
    maximumFractionDigits: 0 
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function OnboardingReview() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [users, setUsers] = useState([]);
  
  // Decision dialog
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [decision, setDecision] = useState('');
  const [decisionComment, setDecisionComment] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [limiteDiario, setLimiteDiario] = useState('');
  const [limiteMensual, setLimiteMensual] = useState('');
  
  // Forward dialog
  const [showForwardDialog, setShowForwardDialog] = useState(false);
  const [forwardTo, setForwardTo] = useState('');
  const [forwardComment, setForwardComment] = useState('');
  
  // Document decision
  const [showDocDecisionDialog, setShowDocDecisionDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [docDecision, setDocDecision] = useState('');
  
  // Document viewer
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentContent, setDocumentContent] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [docDecisionComment, setDocDecisionComment] = useState('');
  
  // Comment
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);

  // Extender vencimiento de clave temporal
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [extendHours, setExtendHours] = useState(168);
  const [extendNoExpiration, setExtendNoExpiration] = useState(false);
  const [extending, setExtending] = useState(false);

  // Ver / Cambiar credenciales de acceso del cliente
  const [showCredsDialog, setShowCredsDialog] = useState(false);
  const [credsInfo, setCredsInfo] = useState(null);
  const [credsLoading, setCredsLoading] = useState(false);
  const [credsCustomPassword, setCredsCustomPassword] = useState('');
  const [credsSendEmail, setCredsSendEmail] = useState(false);
  const [credsSetting, setCredsSetting] = useState(false);
  const [credsResult, setCredsResult] = useState(null); // { temp_password, portal_link, email_sent }
  
  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Configuración de aprobación
  const [approvalConfig, setApprovalConfig] = useState({ niveles_aprobacion: 1, require_different_approvers: false });

  // Creación de cuentas jurídicas
  const [crearCuentas, setCrearCuentas] = useState(false);
  const [numCuentas, setNumCuentas] = useState(1);
  const [datosCompartidos, setDatosCompartidos] = useState({
    cuit: '', actividad: '', inicio_actividad: '', celular: '',
    pep_uif: 'NO', password: '', pin: ''
  });
  const [cuentasIndividuales, setCuentasIndividuales] = useState([]);
  const [mostrarPassword, setMostrarPassword] = useState(false);

  // Generar cuentas individuales cuando cambia el numero
  const generarCuentasIndividuales = (cantidad, razonSocialBase, emailBase) => {
    const nuevas = [];
    for (let i = 1; i <= cantidad; i++) {
      const existente = cuentasIndividuales[i - 1];
      let razon = existente?.razon_social;
      let email = existente?.email;
      if (!razon) {
        razon = razonSocialBase ? `${razonSocialBase} ${i}` : `Cuenta ${i}`;
      }
      if (!email) {
        if (emailBase && emailBase.includes('@')) {
          const [user, domain] = emailBase.split('@');
          email = `${user}${i}@${domain}`;
        } else {
          email = '';
        }
      }
      nuevas.push({ razon_social: razon, email });
    }
    setCuentasIndividuales(nuevas);
  };

  const handleNumCuentasChange = (val) => {
    const n = Math.max(1, Math.min(20, parseInt(val) || 1));
    setNumCuentas(n);
    const razonBase = request?.empresa_data?.razon_social || request?.empresa_nombre_inicial || '';
    const emailBase = request?.empresa_data?.email || request?.representante_email || '';
    generarCuentasIndividuales(n, razonBase, emailBase);
  };

  const updateCuentaIndividual = (idx, field, value) => {
    setCuentasIndividuales(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const agregarCuenta = () => {
    const n = numCuentas + 1;
    setNumCuentas(n);
    const razonBase = request?.empresa_data?.razon_social || request?.empresa_nombre_inicial || '';
    const emailBase = request?.empresa_data?.email || request?.representante_email || '';
    const [user, domain] = emailBase.includes('@') ? emailBase.split('@') : [emailBase, ''];
    setCuentasIndividuales(prev => [...prev, {
      razon_social: razonBase ? `${razonBase} ${n}` : `Cuenta ${n}`,
      email: domain ? `${user}${n}@${domain}` : ''
    }]);
  };

  const quitarCuenta = (idx) => {
    if (cuentasIndividuales.length <= 1) return;
    setCuentasIndividuales(prev => prev.filter((_, i) => i !== idx));
    setNumCuentas(prev => prev - 1);
  };

  const initCuentasState = () => {
    setCrearCuentas(false);
    setNumCuentas(1);
    setDatosCompartidos({
      cuit: request?.empresa_data?.cuit || request?.representante_cuit || '',
      actividad: request?.empresa_data?.actividad_principal || '',
      inicio_actividad: '',
      celular: request?.empresa_data?.telefono || '',
      pep_uif: 'NO', password: '', pin: ''
    });
    setCuentasIndividuales([]);
    setMostrarPassword(false);
  };

  useEffect(() => {
    loadRequest();
    loadUsers();
    loadApprovalConfig();
  }, [requestId]);

  const loadApprovalConfig = async () => {
    try {
      const res = await api.get('/onboarding/config/aprobacion');
      setApprovalConfig(res.data);
    } catch (error) {
      console.error('Error cargando config de aprobación:', error);
    }
  };

  const loadRequest = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/onboarding/requests/${requestId}`);
      setRequest(res.data);
    } catch (error) {
      toast.error('Error al cargar solicitud');
      navigate('/m/empresas/solicitudes-onboarding');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      // Cargar usuarios disponibles según jerarquía
      const res = await api.get('/groups/derivation/available-users');
      setUsers(res.data?.usuarios_disponibles?.filter(u => 
        !u.es_mismo_usuario  // Excluir usuario actual
      ) || []);
    } catch (error) {
      console.error('Error loading users:', error);
      // Fallback a lista de usuarios tradicional
      try {
        const fallback = await api.get('/users/');
        setUsers(fallback.data?.filter(u => 
          ['ADMINISTRADOR', 'SUPERVISOR', 'COMPLIANCE'].includes(u.rol) && u.activo
        ) || []);
      } catch (e) {
        console.error('Error loading fallback users:', e);
      }
    }
  };

  const handleDecision = async () => {
    // Validación para rechazo, info adicional y devolución
    if (['RECHAZADO', 'INFO_ADICIONAL', 'NIVEL_1_DEVUELTO'].includes(decision) && !decisionComment.trim()) {
      toast.error('Complete el motivo');
      return;
    }
    
    // Validación para aprobación - requiere límites
    if ((decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO') && (!limiteDiario || !limiteMensual)) {
      toast.error('Debe ingresar los límites diario y mensual');
      return;
    }
    
    const diario = parseFloat(limiteDiario);
    const mensual = parseFloat(limiteMensual);
    
    if ((decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO') && diario > mensual) {
      toast.error('El límite diario no puede ser mayor al mensual');
      return;
    }
    
    setDeciding(true);
    try {
      const payload = {
        decision,
        comentario: decisionComment
      };
      
      // Agregar límites si es aprobación
      if (decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO') {
        payload.limites = {
          diario: diario,
          mensual: mensual
        };
      }
      
      
      await api.post(`/onboarding/requests/${request.id}/decide`, payload);
      
      // Si se marcó creación de cuentas, enviarlas al backend
      if (crearCuentas && cuentasIndividuales.length > 0 && (decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO')) {
        try {
          await api.post('/cuentas-juridicas/crear', {
            onboarding_request_id: request.id,
            datos_compartidos: datosCompartidos,
            cuentas: cuentasIndividuales
          });
          toast.success(`${cuentasIndividuales.length} cuenta(s) enviadas a creación`);
        } catch (cuentaError) {
          console.error('Error al crear cuentas:', cuentaError);
          toast.error('Aprobación OK, pero hubo un error al enviar las cuentas: ' + (cuentaError.response?.data?.detail || cuentaError.message));
        }
      }
      
      const messages = {
        'NIVEL_1_APROBADO': 'Solicitud aprobada (Nivel 1). Pendiente de aprobación final.',
        'APROBADO': 'Solicitud aprobada definitivamente. La empresa fue creada.',
        'RECHAZADO': 'Solicitud rechazada.',
        'INFO_ADICIONAL': 'Se solicitó información adicional al cliente.',
        'NIVEL_1_DEVUELTO': 'Solicitud devuelta al primer revisor.'
      };
      toast.success(messages[decision] || 'Decisión procesada');
      setShowDecisionDialog(false);
      setLimiteDiario('');
      setLimiteMensual('');
      setDecisionComment('');
      loadRequest();
    } catch (error) {
      console.error('[OnboardingReview v2.1] Error en decisión:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar decisión');
    } finally {
      setDeciding(false);
    }
  };

  const handleForward = async () => {
    if (!forwardTo) {
      toast.error('Seleccione un usuario');
      return;
    }
    
    try {
      // Usar el nuevo endpoint de asignación que envía notificación por email
      await api.post(`/onboarding/requests/${request.id}/assign`, {
        usuario_id: forwardTo,
        motivo: forwardComment || null
      });
      toast.success('Solicitud asignada correctamente. Se envió notificación por email.');
      setShowForwardDialog(false);
      setForwardTo('');
      setForwardComment('');
      loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar solicitud');
    }
  };

  const handleDocumentDecision = async () => {
    if (!docDecision) {
      toast.error('Seleccione una decisión');
      return;
    }
    
    try {
      await api.post(`/onboarding/requests/${request.id}/documents/${selectedDocument.id}/decide`, {
        decision: docDecision,
        comentario: docDecisionComment
      });
      toast.success(`Documento ${docDecision === 'INVALIDO' ? 'marcado como inválido' : docDecision.toLowerCase()}`);
      setShowDocDecisionDialog(false);
      setSelectedDocument(null);
      setDocDecision('');
      setDocDecisionComment('');
      loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar documento');
    }
  };

  const handleDocumentDecisionDirect = async (documentId, decision, comentario) => {
    try {
      await api.post(`/onboarding/requests/${request.id}/documents/${documentId}/decide`, {
        decision,
        comentario
      });
      toast.success(`Documento ${decision === 'INVALIDO' ? 'marcado como inválido' : decision.toLowerCase()}`);
      loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar documento');
    }
  };

  // Función para ver documento
  const handleViewDocument = async (doc) => {
    setViewingDocument(doc);
    setShowDocViewer(true);
    setLoadingDocument(true);
    setDocumentContent(null);
    
    try {
      // Intentar obtener el contenido del documento
      const response = await api.get(`/onboarding/document/${doc.id}`, {
        responseType: 'blob'
      });
      
      // Crear URL del blob para visualizar
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      setDocumentContent({
        url,
        type: doc.content_type || 'application/pdf',
        filename: doc.filename
      });
    } catch (error) {
      console.error('Error loading document:', error);
      // Si falla, intentar con el data inline si existe
      if (doc.data) {
        const dataUrl = `data:${doc.content_type || 'application/pdf'};base64,${doc.data}`;
        setDocumentContent({
          url: dataUrl,
          type: doc.content_type || 'application/pdf',
          filename: doc.filename
        });
      } else {
        toast.error('No se pudo cargar el documento. Es posible que no tenga contenido guardado.');
      }
    } finally {
      setLoadingDocument(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await api.post(`/onboarding/requests/${request.id}/comment`, {
        message: newComment,
        is_internal: isInternalComment
      });
      toast.success('Comentario agregado');
      setNewComment('');
      loadRequest();
    } catch (error) {
      toast.error('Error al agregar comentario');
    }
  };

  const handleResendCredentials = async () => {
    try {
      await api.post(`/onboarding/requests/${request.id}/resend-credentials`);
      toast.success('Credenciales reenviadas');
    } catch (error) {
      toast.error('Error al reenviar credenciales');
    }
  };

  const handleExtendPassword = async () => {
    setExtending(true);
    try {
      const res = await api.post(`/onboarding/requests/${request.id}/extend-password`, {
        expiration_hours: extendNoExpiration ? null : extendHours,
        no_expiration: extendNoExpiration,
      });
      toast.success(res.data.message || 'Vencimiento actualizado');
      setShowExtendDialog(false);
      await loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar el vencimiento');
    } finally {
      setExtending(false);
    }
  };

  const openCredsDialog = async () => {
    setCredsResult(null);
    setCredsCustomPassword('');
    setCredsSendEmail(false);
    setCredsInfo(null);
    setShowCredsDialog(true);
    setCredsLoading(true);
    try {
      const res = await api.get(`/onboarding/requests/${request.id}/credentials`);
      setCredsInfo(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al obtener credenciales');
    } finally {
      setCredsLoading(false);
    }
  };

  const handleSetPassword = async () => {
    setCredsSetting(true);
    try {
      const res = await api.post(`/onboarding/requests/${request.id}/set-password`, {
        password: credsCustomPassword.trim() || null,
        send_email: credsSendEmail,
      });
      setCredsResult(res.data);
      if (res.data.email_sent) {
        toast.success('Contraseña actualizada y reenviada por email');
      } else if (credsSendEmail && res.data.email_error) {
        toast.warning('Contraseña actualizada, pero falló el envío de email');
      } else {
        toast.success('Contraseña temporal actualizada');
      }
      setCredsInfo((prev) => prev ? { ...prev, password_expires_at: res.data.password_expires_at, has_expiration: res.data.has_expiration, is_expired: false } : prev);
      await loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al setear la contraseña');
    } finally {
      setCredsSetting(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copiado`),
      () => toast.error('No se pudo copiar')
    );
  };

  const handleEditRequest = () => {
    setEditData({
      representante_nombre: request.representante_nombre,
      representante_email: request.representante_email,
      empresa_data: { ...request.empresa_data },
      limites: { ...request.limites }
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/onboarding/requests/${request.id}`, editData);
      toast.success('Solicitud actualizada');
      setShowEditDialog(false);
      loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!window.confirm('¿Está seguro de eliminar esta solicitud? Esta acción no se puede deshacer.')) return;
    
    try {
      await api.delete(`/onboarding/requests/${request.id}`);
      toast.success('Solicitud eliminada');
      navigate('/m/empresas/solicitudes-onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getEstadoInfo = (estado) => ESTADOS[estado] || ESTADOS.PENDIENTE;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-600" size={40} />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Solicitud no encontrada</p>
      </div>
    );
  }

  const estadoInfo = getEstadoInfo(request.estado);
  const EstadoIcon = estadoInfo.icon;
  const representantePadron = request.representante_padron;
  
  // IMPORTANTE: Determinar si puede tomar decisiones basándose en el estado
  // Si el estado es NIVEL_1_APROBADO, SIEMPRE mostrar el panel de nivel 2
  const nivelesAprobacion = approvalConfig.niveles_aprobacion || 2; // Default a 2 para ser conservador
  const estadosNivel1 = ['ENVIADO', 'EN_REVISION', 'PENDIENTE', 'EN_PROGRESO'];
  const canDecideNivel1 = estadosNivel1.includes(request.estado);
  // CRÍTICO: Si está en NIVEL_1_APROBADO, siempre permitir nivel 2 (independiente de config)
  const canDecideNivel2 = request.estado === 'NIVEL_1_APROBADO';
  const canDecide = canDecideNivel1 || canDecideNivel2;
  const needsForcedReview = ['PENDIENTE', 'EN_PROGRESO'].includes(request.estado);
  
  // Determinar qué tipo de aprobación mostrar
  // Si está en NIVEL_1_APROBADO, mostrar nivel 2
  // Si está en ENVIADO y config es 1 nivel, mostrar aprobación simple
  // Si está en ENVIADO y config es 2 niveles, mostrar nivel 1
  const showLevel2Approval = canDecideNivel2;
  const showSingleLevelApproval = canDecideNivel1 && nivelesAprobacion === 1 && !canDecideNivel2;
  const showLevel1Approval = canDecideNivel1 && nivelesAprobacion === 2 && !canDecideNivel2;

  // Log para debugging

  return (
    <div className="bg-slate-50 p-6 pb-20">
      {/* Version indicator for debugging - v2.2 */}
      <div className="fixed bottom-2 right-2 text-xs text-slate-400 bg-white/80 px-2 py-1 rounded shadow-sm z-50">
        v2.2-{new Date().toISOString().slice(5,10)}
      </div>
      
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/m/empresas/solicitudes-onboarding')}
          className="mb-4"
        >
          <ArrowLeft size={16} className="mr-2" />
          Volver a Solicitudes
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-slate-900">
                {request.empresa_data?.razon_social || request.empresa_nombre_inicial}
              </h1>
              <Badge className={estadoInfo.color}>
                <EstadoIcon size={14} className="mr-1" />
                {estadoInfo.label}
              </Badge>
            </div>
            <p className="text-slate-500">
              Solicitud creada el {formatDate(request.created_at)} por {request.created_by_email}
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadRequest}>
              <RefreshCw size={16} className="mr-2" />
              Actualizar
            </Button>
            <Button variant="outline" onClick={() => setShowForwardDialog(true)}>
              <Forward size={16} className="mr-2" />
              Derivar
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Indicador de Verificación del Padrón */}
          {representantePadron && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle size={18} className="text-green-600" />
              <span className="text-sm font-medium text-green-800">
                Representante Legal verificado en el Padrón (CUIT: {representantePadron.cuit})
              </span>
            </div>
          )}

          {/* Datos del Representante (del padrón o de la solicitud) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <User size={20} className="text-blue-600" />
                Datos de Contacto (Representante Legal)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-slate-500">Nombre</Label>
                  <p className="font-medium text-slate-900">
                    {representantePadron 
                      ? `${representantePadron.nombre || ''} ${representantePadron.apellido || ''}`.trim() 
                      : request.representante_nombre || '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p className="font-medium flex items-center gap-2 text-slate-900">
                    <Mail size={14} className="text-slate-400" />
                    {representantePadron?.mail || request.representante_email}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">CUIT Representante</Label>
                  <p className="font-medium font-mono text-slate-900">
                    {representantePadron?.cuit || request.representante_cuit || '-'}
                  </p>
                </div>
                {(representantePadron?.telefono || request.telefono) && (
                  <div>
                    <Label className="text-xs text-slate-500">Teléfono</Label>
                    <p className="font-medium flex items-center gap-2 text-slate-900">
                      <Phone size={14} className="text-slate-400" />
                      {representantePadron?.telefono || request.telefono || '-'}
                    </p>
                  </div>
                )}
                {representantePadron?.dni && (
                  <div>
                    <Label className="text-xs text-slate-500">DNI</Label>
                    <p className="font-medium text-slate-900">{representantePadron.dni}</p>
                  </div>
                )}
                {representantePadron?.domicilio && (
                  <div className="col-span-3">
                    <Label className="text-xs text-slate-500">Domicilio</Label>
                    <p className="font-medium flex items-center gap-2 text-slate-900">
                      <MapPin size={14} className="text-slate-400" />
                      {representantePadron.domicilio}
                      {representantePadron.localidad && `, ${representantePadron.localidad}`}
                      {representantePadron.provincia && `, ${representantePadron.provincia}`}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Verificación AFIP del Representante */}
              <div className="mt-4 pt-4 border-t">
                <Label className="text-xs text-slate-500 mb-2 block">Verificación AFIP</Label>
                <AFIPVerification 
                  cuit={representantePadron?.cuit || request.representante_cuit}
                  variant="card"
                  showDetails={true}
                />
              </div>
            </CardContent>
          </Card>

          {/* Datos de la Empresa */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 size={20} className="text-purple-600" />
                Datos de la Empresa
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-slate-500">Razón Social</Label>
                  <p className="font-medium">{request.empresa_data?.razon_social || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">CUIT</Label>
                  <p className="font-medium font-mono">{request.empresa_data?.cuit || '-'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Domicilio</Label>
                  <p className="font-medium flex items-center gap-2">
                    <MapPin size={14} className="text-slate-400" />
                    {request.empresa_data?.domicilio || '-'}, {request.empresa_data?.localidad}, {request.empresa_data?.provincia}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Teléfono</Label>
                  <p className="font-medium flex items-center gap-2">
                    <Phone size={14} className="text-slate-400" />
                    {request.empresa_data?.telefono || '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p className="font-medium">{request.empresa_data?.email || '-'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Actividad Principal</Label>
                  <p className="font-medium">{request.empresa_data?.actividad_principal || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Límites */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign size={20} className="text-blue-600" />
                Límites Solicitados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Límite Diario</p>
                  <p className="text-3xl font-bold text-blue-700">{formatCurrency(request.limites?.diario)}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Límite Mensual</p>
                  <p className="text-3xl font-bold text-blue-700">{formatCurrency(request.limites?.mensual)}</p>
                </div>
              </div>
              {request.limites?.justificacion && (
                <div className="mt-4 p-3 bg-white rounded-lg">
                  <Label className="text-xs text-slate-500">Justificación</Label>
                  <p className="text-sm">{request.limites.justificacion}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documentos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText size={20} className="text-amber-600" />
                Documentación
              </CardTitle>
              {request.documentos?.length > 0 && (
                <button
                  onClick={async () => {
                    try {
                      const response = await api.get(`/onboarding/requests/${request.id}/documents/download-all`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(response.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `documentos_${request.empresa_data?.razon_social?.replace(/\s+/g, '_') || 'onboarding'}.zip`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                      toast.success('Documentos descargados');
                    } catch {
                      toast.error('Error al descargar documentos');
                    }
                  }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100"
                  data-testid="onboarding-download-all-docs"
                >
                  <Download size={14} />
                  Descargar Todo
                </button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {request.documentos_requeridos?.map(doc => {
                  const uploaded = request.documentos?.find(d => d.tipo_documento_id === doc.id);
                  
                  return (
                    <div 
                      key={doc.id}
                      className={`p-4 border rounded-lg ${
                        uploaded?.estado === 'APROBADO' ? 'border-green-300 bg-green-50' :
                        uploaded?.estado === 'RECHAZADO' ? 'border-red-300 bg-red-50' :
                        uploaded?.estado === 'INVALIDO' ? 'border-orange-300 bg-orange-50' :
                        doc.subido ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {uploaded?.estado === 'APROBADO' ? (
                            <CheckCircle className="text-green-600 mt-1" size={24} />
                          ) : uploaded?.estado === 'RECHAZADO' ? (
                            <XCircle className="text-red-600 mt-1" size={24} />
                          ) : uploaded?.estado === 'INVALIDO' ? (
                            <AlertTriangle className="text-orange-600 mt-1" size={24} />
                          ) : doc.subido ? (
                            <FileText className="text-blue-600 mt-1" size={24} />
                          ) : (
                            <FileText className="text-slate-400 mt-1" size={24} />
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{doc.nombre}</p>
                            {uploaded && (
                              <>
                                <p className="text-sm text-slate-500">
                                  {uploaded.filename} • {(uploaded.size / 1024).toFixed(1)} KB
                                </p>
                                {uploaded.decision_comentario && (
                                  <p className="text-sm text-slate-600 mt-1 italic bg-white/50 px-2 py-1 rounded">
                                    "{uploaded.decision_comentario}"
                                  </p>
                                )}
                              </>
                            )}
                            {uploaded?.estado === 'APROBADO' && (
                              <Badge className="mt-2 bg-green-100 text-green-700">Aprobado</Badge>
                            )}
                            {uploaded?.estado === 'RECHAZADO' && (
                              <Badge className="mt-2 bg-red-100 text-red-700">Rechazado</Badge>
                            )}
                            {uploaded?.estado === 'INVALIDO' && (
                              <Badge className="mt-2 bg-orange-100 text-orange-700">Documento Inválido - Requiere nuevo</Badge>
                            )}
                            {!doc.subido && (
                              <Badge variant="outline" className="mt-2 text-amber-600 border-amber-300">Pendiente de carga</Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Botones de acción para documentos */}
                        <div className="flex flex-col gap-2">
                          {/* Botones Ver y Descargar si hay documento */}
                          {uploaded && (
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewDocument(uploaded)}
                                className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                                data-testid={`view-doc-${uploaded.id}`}
                              >
                                <Eye size={14} className="mr-1" />
                                Ver
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/onboarding/document/${uploaded.id}`;
                                  window.open(downloadUrl, '_blank');
                                }}
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                data-testid={`download-doc-${uploaded.id}`}
                              >
                                <Download size={14} className="mr-1" />
                                Descargar
                              </Button>
                            </div>
                          )}
                          
                          {/* Botones de decisión solo si no tiene estado y puede decidir */}
                          {uploaded && !uploaded.estado && canDecide && (
                            <>
                              <button
                                onClick={() => handleDocumentDecisionDirect(uploaded.id, 'APROBADO', '')}
                                className="px-3 py-1.5 text-sm rounded-md flex items-center gap-1"
                                style={{ backgroundColor: '#16a34a', color: '#ffffff' }}
                              >
                                <CheckCircle size={14} />
                                Aprobar
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedDocument(uploaded);
                                  setDocDecision('INVALIDO');
                                  setDocDecisionComment('');
                                  setShowDocDecisionDialog(true);
                                }}
                                className="px-3 py-1.5 text-sm rounded-md flex items-center gap-1"
                                style={{ backgroundColor: '#ea580c', color: '#ffffff' }}
                              >
                                <AlertTriangle size={14} />
                                Inválido
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedDocument(uploaded);
                                  setDocDecision('RECHAZADO');
                                  setDocDecisionComment('');
                                  setShowDocDecisionDialog(true);
                                }}
                                className="px-3 py-1.5 text-sm rounded-md flex items-center gap-1"
                                style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
                              >
                                <XCircle size={14} />
                                Rechazar
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Nivel 2 - Card para NIVEL_1_APROBADO (solo si hay 2 niveles) */}
          {showLevel2Approval && (
            <Card className="border-2 border-green-300 bg-green-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-green-800 flex items-center gap-2">
                  <CheckCircle size={20} />
                  Aprobación Final (Nivel 2)
                </CardTitle>
                <p className="text-xs text-green-700 mt-1">
                  Esta solicitud fue aprobada en Nivel 1. Puede aprobar definitivamente o devolver.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Info de nivel 1 */}
                <div className="bg-white rounded-lg p-3 border border-green-200 text-sm">
                  <p className="font-medium text-green-800 mb-1">Límites propuestos por Nivel 1:</p>
                  <p className="text-green-700">
                    Diario: ${request.nivel_1?.limites_propuestos?.diario?.toLocaleString('es-AR') || '0'}
                  </p>
                  <p className="text-green-700">
                    Mensual: ${request.nivel_1?.limites_propuestos?.mensual?.toLocaleString('es-AR') || '0'}
                  </p>
                  <p className="text-xs text-green-600 mt-2">
                    Aprobado por: {request.nivel_1?.aprobado_por_email}
                  </p>
                </div>
                
                <button
                  className="w-full py-2.5 px-4 rounded-md flex items-center justify-center gap-2 font-medium bg-green-600 text-white hover:bg-green-700"
                  onClick={() => {
                    setDecision('APROBADO');
                    setDecisionComment('');
                    setLimiteDiario(request.nivel_1?.limites_propuestos?.diario?.toString() || '');
                    setLimiteMensual(request.nivel_1?.limites_propuestos?.mensual?.toString() || '');
                    initCuentasState();
                    setShowDecisionDialog(true);
                  }}
                  data-testid="aprobar-nivel2-btn"
                >
                  <CheckCircle size={18} />
                  Aprobar Definitivo
                </button>
                <button
                  className="w-full py-2.5 px-4 rounded-md flex items-center justify-center gap-2 font-medium bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-200"
                  onClick={() => {
                    setDecision('NIVEL_1_DEVUELTO');
                    setDecisionComment('');
                    setShowDecisionDialog(true);
                  }}
                  data-testid="devolver-nivel1-btn"
                >
                  <AlertTriangle size={18} />
                  Devolver a Nivel 1
                </button>
              </CardContent>
            </Card>
          )}

          {/* Aprobación Simple (1 nivel) */}
          {showSingleLevelApproval && (
            <Card className="border-2 border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle size={20} className="text-green-600" />
                  Tomar Decisión
                </CardTitle>
                {needsForcedReview && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ El cliente aún no envió la documentación completa
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <button
                  className="w-full py-2.5 px-4 rounded-md flex items-center justify-center gap-2 font-medium bg-green-600 text-white hover:bg-green-700"
                  onClick={() => {
                    setDecision('APROBADO');
                    setDecisionComment('');
                    initCuentasState();
                    setShowDecisionDialog(true);
                  }}
                  data-testid="aprobar-solicitud-btn"
                >
                  <CheckCircle size={18} />
                  Aprobar
                </button>
                <button
                  className="w-full py-2.5 px-4 rounded-md flex items-center justify-center gap-2 font-medium bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200"
                  onClick={() => {
                    setDecision('INFO_ADICIONAL');
                    setDecisionComment('');
                    setShowDecisionDialog(true);
                  }}
                  data-testid="solicitar-info-btn"
                >
                  <AlertTriangle size={18} />
                  Solicitar Info
                </button>
                <button
                  className="w-full py-2.5 px-4 rounded-md flex items-center justify-center gap-2 font-medium bg-red-600 text-white hover:bg-red-700"
                  onClick={() => {
                    setDecision('RECHAZADO');
                    setDecisionComment('');
                    setShowDecisionDialog(true);
                  }}
                  data-testid="rechazar-solicitud-btn"
                >
                  <XCircle size={18} />
                  Rechazar
                </button>
              </CardContent>
            </Card>
          )}

          {/* Nivel 1 de 2 - Actions Card */}
          {showLevel1Approval && (
            <Card className="border-2 border-purple-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Tomar Decisión (Nivel 1 de 2)</CardTitle>
                {needsForcedReview && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ El cliente aún no envió la documentación completa
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Después de aprobar, otro revisor dará la aprobación final.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <button
                  className="w-full py-2.5 px-4 rounded-md flex items-center justify-center gap-2 font-medium bg-green-600 text-white hover:bg-green-700"
                  onClick={() => {
                    setDecision('NIVEL_1_APROBADO');
                    setDecisionComment('');
                    initCuentasState();
                    setShowDecisionDialog(true);
                  }}
                  data-testid="aprobar-nivel1-btn"
                >
                  <CheckCircle size={18} />
                  Aprobar (Nivel 1)
                </button>
                <button
                  className="w-full py-2.5 px-4 rounded-md flex items-center justify-center gap-2 font-medium bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200"
                  onClick={() => {
                    setDecision('INFO_ADICIONAL');
                    setDecisionComment('');
                    setShowDecisionDialog(true);
                  }}
                  data-testid="solicitar-info-btn"
                >
                  <AlertTriangle size={18} />
                  Solicitar Info
                </button>
                <button
                  className="w-full py-2.5 px-4 rounded-md flex items-center justify-center gap-2 font-medium bg-red-600 text-white hover:bg-red-700"
                  onClick={() => {
                    setDecision('RECHAZADO');
                    setDecisionComment('');
                    setShowDecisionDialog(true);
                  }}
                  data-testid="rechazar-solicitud-btn"
                >
                  <XCircle size={18} />
                  Rechazar
                </button>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Acciones</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={handleResendCredentials}>
                <Key size={16} className="mr-2" />
                Reenviar Credenciales
              </Button>
              <Button variant="outline" className="w-full justify-start" data-testid="extend-password-btn"
                onClick={() => {
                  const noExp = !request?.password_expires_at;
                  setExtendNoExpiration(noExp);
                  setExtendHours(168);
                  setShowExtendDialog(true);
                }}>
                <Clock size={16} className="mr-2" />
                Extender / Quitar Vencimiento
              </Button>
              <Button variant="outline" className="w-full justify-start" data-testid="ver-credenciales-btn"
                onClick={openCredsDialog}>
                <Link2 size={16} className="mr-2" />
                Ver / Cambiar Acceso
              </Button>
              <p className="text-xs text-slate-500 px-1" data-testid="password-expiry-status">
                {request?.password_expires_at
                  ? `Clave vence: ${new Date(request.password_expires_at).toLocaleString('es-AR')}`
                  : 'Clave temporal: sin vencimiento'}
              </p>
              <Button variant="outline" className="w-full justify-start" onClick={handleEditRequest}>
                <Edit2 size={16} className="mr-2" />
                Modificar Datos
              </Button>
              <button 
                className="w-full py-2 px-4 rounded-md flex items-center justify-start gap-2 border"
                style={{ backgroundColor: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' }}
                onClick={handleDeleteRequest}
              >
                <Trash2 size={16} />
                Eliminar Solicitud
              </button>
            </CardContent>
          </Card>

          {/* Historial */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <History size={18} />
                Historial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {request.historial_estados?.map((h, idx) => {
                  const info = getEstadoInfo(h.estado);
                  const Icon = info.icon;
                  return (
                    <div key={idx} className="flex gap-3 pb-3 border-b last:border-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${info.color}`}>
                        <Icon size={14} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">{info.label}</p>
                        <p className="text-xs text-slate-500">{formatDate(h.fecha)}</p>
                        <p className="text-xs text-slate-400">{h.usuario}</p>
                        {h.comentario && (
                          <p className="text-xs text-slate-600 mt-1 italic">"{h.comentario}"</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Comentarios */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare size={18} />
                Comentarios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
                {request.comentarios?.length === 0 ? (
                  <p className="text-center text-slate-400 text-sm py-4">Sin comentarios</p>
                ) : (
                  request.comentarios?.map(c => (
                    <div 
                      key={c.id} 
                      className={`p-2 rounded-lg text-sm ${
                        c.is_internal ? 'bg-amber-50 border border-amber-200' :
                        c.user_id === 'client' ? 'bg-blue-50' : 'bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium">{c.user_name}</span>
                        {c.is_internal && <Badge className="text-xs bg-amber-100 text-amber-700 px-1">Interno</Badge>}
                      </div>
                      <p className="text-xs">{c.message}</p>
                      <p className="text-xs text-slate-400 mt-1">{formatDate(c.created_at)}</p>
                    </div>
                  ))
                )}
              </div>
              
              <div className="border-t pt-3 space-y-2">
                <Textarea
                  placeholder="Escribir comentario..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={2}
                  className="text-sm"
                />
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={isInternalComment}
                      onChange={(e) => setIsInternalComment(e.target.checked)}
                    />
                    <span className="text-slate-600">Interno</span>
                  </label>
                  <Button size="sm" onClick={handleAddComment}>Enviar</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Decision Dialog */}
      {/* Dialog: Extender / quitar vencimiento de clave temporal */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }} data-testid="extend-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock size={18} /> Vencimiento de la clave temporal
            </DialogTitle>
            <DialogDescription>
              Ajustá la duración de la clave sin regenerar la contraseña actual.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Estado actual:{' '}
              <strong>
                {request?.password_expires_at
                  ? `vence ${new Date(request.password_expires_at).toLocaleString('es-AR')}`
                  : 'sin vencimiento'}
              </strong>
            </p>
            <div>
              <Label>Nueva duración (desde ahora)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  className="w-28"
                  data-testid="extend-hours-input"
                  disabled={extendNoExpiration}
                  value={extendNoExpiration ? '' : extendHours}
                  onChange={(e) => setExtendHours(parseInt(e.target.value) || 0)}
                />
                <span className="text-slate-500">horas</span>
                <div className="flex gap-1 ml-2">
                  {[{ l: '24h', v: 24 }, { l: '7 días', v: 168 }, { l: '30 días', v: 720 }].map((p) => (
                    <Button key={p.v} type="button" variant="outline" size="sm" className="h-7 text-xs"
                      disabled={extendNoExpiration}
                      onClick={() => setExtendHours(p.v)} data-testid={`extend-preset-${p.v}`}>
                      {p.l}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                data-testid="extend-no-expiration-checkbox"
                checked={extendNoExpiration}
                onChange={(e) => setExtendNoExpiration(e.target.checked)}
              />
              Sin vencimiento (la clave nunca expira)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtendDialog(false)} disabled={extending} data-testid="extend-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={handleExtendPassword} disabled={extending} data-testid="extend-confirm-btn">
              {extending ? <><RefreshCw size={14} className="animate-spin mr-1" /> Guardando...</> : 'Aplicar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Ver / Cambiar credenciales de acceso */}
      <Dialog open={showCredsDialog} onOpenChange={(o) => { if (!credsSetting) setShowCredsDialog(o); }}>
        <DialogContent style={{ backgroundColor: '#ffffff' }} className="max-w-lg" data-testid="credenciales-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 size={18} /> Acceso del cliente al portal
            </DialogTitle>
            <DialogDescription>
              Probá el acceso del cliente: copiá el link, seteá una contraseña y decidí si la reenviás por email.
            </DialogDescription>
          </DialogHeader>

          {credsLoading ? (
            <div className="py-8 text-center text-slate-500" data-testid="creds-loading">
              <RefreshCw size={22} className="animate-spin mx-auto mb-2" /> Cargando...
            </div>
          ) : credsInfo ? (
            <div className="space-y-4">
              {/* Link de acceso */}
              <div>
                <Label className="text-xs text-slate-500">Link de acceso</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input readOnly value={credsInfo.portal_link || ''} className="text-xs font-mono" data-testid="creds-portal-link"
                    style={{ backgroundColor: '#f8fafc' }} />
                  <Button type="button" variant="outline" size="sm" className="h-9 px-2" title="Copiar link"
                    onClick={() => copyToClipboard(credsInfo.portal_link, 'Link')} data-testid="creds-copy-link-btn">
                    <Copy size={14} />
                  </Button>
                  {credsInfo.portal_link && (
                    <a href={credsInfo.portal_link} target="_blank" rel="noreferrer">
                      <Button type="button" variant="outline" size="sm" className="h-9 px-2" title="Abrir en nueva pestaña" data-testid="creds-open-link-btn">
                        <ExternalLink size={14} />
                      </Button>
                    </a>
                  )}
                </div>
              </div>

              {/* Email de acceso */}
              <div>
                <Label className="text-xs text-slate-500">Email de acceso</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input readOnly value={credsInfo.email || ''} className="text-sm font-mono" data-testid="creds-email"
                    style={{ backgroundColor: '#f8fafc' }} />
                  <Button type="button" variant="outline" size="sm" className="h-9 px-2" title="Copiar email"
                    onClick={() => copyToClipboard(credsInfo.email, 'Email')} data-testid="creds-copy-email-btn">
                    <Copy size={14} />
                  </Button>
                </div>
                <p className="text-xs mt-1.5" data-testid="creds-expiry-status">
                  {credsInfo.has_expiration
                    ? (credsInfo.is_expired
                        ? <span className="text-red-600 font-medium">Clave actual VENCIDA ({new Date(credsInfo.password_expires_at).toLocaleString('es-AR')})</span>
                        : <span className="text-slate-500">Clave vence: {new Date(credsInfo.password_expires_at).toLocaleString('es-AR')}</span>)
                    : <span className="text-slate-500">Clave temporal sin vencimiento</span>}
                </p>
              </div>

              {/* Resultado de set-password */}
              {credsResult ? (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="creds-result">
                  <p className="text-xs text-emerald-700 uppercase font-medium mb-1">Nueva contraseña temporal</p>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={credsResult.temp_password} className="text-sm font-mono font-semibold" data-testid="creds-new-password"
                      style={{ backgroundColor: '#ffffff' }} />
                    <Button type="button" variant="outline" size="sm" className="h-9 px-2" title="Copiar contraseña"
                      onClick={() => copyToClipboard(credsResult.temp_password, 'Contraseña')} data-testid="creds-copy-password-btn">
                      <Copy size={14} />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {credsResult.email_sent
                      ? 'Reenviada por email al cliente.'
                      : 'No se envió por email. Copiala para probar el login manualmente.'}
                  </p>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Setear contraseña (opcional)</Label>
                    <Input
                      value={credsCustomPassword}
                      onChange={(e) => setCredsCustomPassword(e.target.value)}
                      placeholder="Dejar vacío para generar una aleatoria"
                      className="text-sm mt-1"
                      data-testid="creds-custom-password-input"
                      style={{ backgroundColor: '#ffffff' }}
                    />
                    <p className="text-[11px] text-slate-400 mt-1">Útil para reproducir un error de login con una clave conocida.</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={credsSendEmail} onChange={(e) => setCredsSendEmail(e.target.checked)} data-testid="creds-send-email-checkbox" />
                    Reenviar credenciales por email al cliente
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div className="py-6 text-center text-sm text-slate-500">No se pudieron cargar las credenciales.</div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredsDialog(false)} disabled={credsSetting} data-testid="creds-close-btn">
              Cerrar
            </Button>
            {credsInfo && !credsResult && (
              <Button onClick={handleSetPassword} disabled={credsSetting} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="creds-set-password-btn">
                {credsSetting ? <><RefreshCw size={14} className="animate-spin mr-1" /> Aplicando...</> : (credsCustomPassword.trim() ? 'Setear contraseña' : 'Generar contraseña')}
              </Button>
            )}
            {credsResult && (
              <Button onClick={() => { setCredsResult(null); setCredsCustomPassword(''); }} variant="outline" data-testid="creds-another-btn">
                Setear otra
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>



      <Dialog open={showDecisionDialog} onOpenChange={setShowDecisionDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle>
              {decision === 'NIVEL_1_APROBADO' && 'Aprobar Solicitud (Nivel 1)'}
              {decision === 'APROBADO' && (approvalConfig.niveles_aprobacion === 1 ? 'Aprobar Solicitud' : 'Aprobar Definitivo (Nivel 2)')}
              {decision === 'RECHAZADO' && 'Rechazar Solicitud'}
              {decision === 'INFO_ADICIONAL' && 'Solicitar Información Adicional'}
              {decision === 'NIVEL_1_DEVUELTO' && 'Devolver a Nivel 1'}
            </DialogTitle>
            <DialogDescription>
              {decision === 'NIVEL_1_APROBADO' && 'Proponga los límites para esta empresa. Luego otro revisor dará la aprobación final.'}
              {decision === 'APROBADO' && (approvalConfig.niveles_aprobacion === 1 
                ? 'Ingrese los límites para esta empresa. La empresa será creada automáticamente.' 
                : 'Confirme o modifique los límites. La empresa será creada automáticamente.')}
              {decision === 'RECHAZADO' && 'Indique el motivo del rechazo. El cliente será notificado.'}
              {decision === 'INFO_ADICIONAL' && 'Indique qué información necesita. El cliente podrá modificar su solicitud.'}
              {decision === 'NIVEL_1_DEVUELTO' && 'Indique el motivo de la devolución. El primer revisor podrá corregir o cerrar la solicitud.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Info de límites propuestos para nivel 2 (solo si hay 2 niveles y estamos en nivel 2) */}
            {decision === 'APROBADO' && approvalConfig.niveles_aprobacion === 2 && request.nivel_1?.limites_propuestos && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-800 mb-1">Límites propuestos por Nivel 1:</p>
                <p className="text-blue-700">
                  Diario: ${request.nivel_1.limites_propuestos.diario?.toLocaleString('es-AR')} | 
                  Mensual: ${request.nivel_1.limites_propuestos.mensual?.toLocaleString('es-AR')}
                </p>
                <p className="text-blue-600 text-xs mt-1">Puede mantenerlos o modificarlos abajo</p>
              </div>
            )}
            
            {/* Campos de límites - obligatorios para NIVEL_1_APROBADO y APROBADO */}
            {(decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO') && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                <h4 className="font-medium text-green-800 flex items-center gap-2">
                  <DollarSign size={16} />
                  Límites a Asignar *
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-green-700">Límite Diario ($)</Label>
                    <Input
                      type="number"
                      placeholder="Ej: 500000"
                      value={limiteDiario}
                      onChange={(e) => setLimiteDiario(e.target.value)}
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-green-700">Límite Mensual ($)</Label>
                    <Input
                      type="number"
                      placeholder="Ej: 5000000"
                      value={limiteMensual}
                      onChange={(e) => setLimiteMensual(e.target.value)}
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                </div>
                {request.limites?.diario && (
                  <p className="text-xs text-green-600">
                    El cliente solicitó: ${request.limites.diario?.toLocaleString('es-AR')} diario / ${request.limites.mensual?.toLocaleString('es-AR')} mensual
                  </p>
                )}
              </div>
            )}
            
            {/* Creación de Cuentas Jurídicas - solo para persona jurídica en aprobación */}
            {(decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO') && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-blue-800 flex items-center gap-2">
                    <Users size={16} />
                    Creación de Cuentas
                  </h4>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={crearCuentas}
                      onChange={(e) => {
                        setCrearCuentas(e.target.checked);
                        if (e.target.checked && cuentasIndividuales.length === 0) {
                          handleNumCuentasChange(1);
                        }
                      }}
                      className="rounded border-blue-300"
                      data-testid="crear-cuentas-checkbox"
                    />
                    <span className="text-sm text-blue-700 font-medium">Crear cuentas al aprobar</span>
                  </label>
                </div>
                
                {crearCuentas && (
                  <div className="space-y-4">
                    {/* Cantidad de cuentas */}
                    <div className="flex items-center gap-3">
                      <Label className="text-xs text-blue-700 whitespace-nowrap">Cantidad de cuentas:</Label>
                      <Input
                        type="number" min="1" max="20"
                        value={numCuentas}
                        onChange={(e) => handleNumCuentasChange(e.target.value)}
                        className="w-20 h-8 text-center"
                        style={{ backgroundColor: '#fff' }}
                        data-testid="num-cuentas-input"
                      />
                    </div>
                    
                    {/* Datos compartidos */}
                    <div className="bg-white rounded-lg border border-blue-100 p-3 space-y-3">
                      <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Datos compartidos (aplican a todas las cuentas)</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-600">CUIT *</Label>
                          <Input value={datosCompartidos.cuit}
                            onChange={(e) => setDatosCompartidos(prev => ({ ...prev, cuit: e.target.value }))}
                            placeholder="20-12345678-9" style={{ backgroundColor: '#fff' }}
                            data-testid="cuenta-cuit" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Actividad *</Label>
                          <Input value={datosCompartidos.actividad}
                            onChange={(e) => setDatosCompartidos(prev => ({ ...prev, actividad: e.target.value }))}
                            placeholder="Ej: Servicios financieros" style={{ backgroundColor: '#fff' }}
                            data-testid="cuenta-actividad" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Inicio de Actividad *</Label>
                          <Input value={datosCompartidos.inicio_actividad}
                            onChange={(e) => setDatosCompartidos(prev => ({ ...prev, inicio_actividad: e.target.value }))}
                            placeholder="17/04/2026" style={{ backgroundColor: '#fff' }}
                            data-testid="cuenta-inicio-actividad" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Celular</Label>
                          <Input value={datosCompartidos.celular}
                            onChange={(e) => setDatosCompartidos(prev => ({ ...prev, celular: e.target.value }))}
                            placeholder="1122334455" style={{ backgroundColor: '#fff' }}
                            data-testid="cuenta-celular" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">PEP/UIF</Label>
                          <Select value={datosCompartidos.pep_uif}
                            onValueChange={(v) => setDatosCompartidos(prev => ({ ...prev, pep_uif: v }))}>
                            <SelectTrigger style={{ backgroundColor: '#fff' }} data-testid="cuenta-pep-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NO">NO</SelectItem>
                              <SelectItem value="SI">SI</SelectItem>
                              <SelectItem value="PEP">PEP</SelectItem>
                              <SelectItem value="UIF">UIF</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">PIN *</Label>
                          <Input value={datosCompartidos.pin}
                            onChange={(e) => setDatosCompartidos(prev => ({ ...prev, pin: e.target.value }))}
                            placeholder="123456" maxLength={6} style={{ backgroundColor: '#fff' }}
                            data-testid="cuenta-pin" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Contraseña *</Label>
                          <div className="relative">
                            <Input type={mostrarPassword ? "text" : "password"}
                              value={datosCompartidos.password}
                              onChange={(e) => setDatosCompartidos(prev => ({ ...prev, password: e.target.value }))}
                              placeholder="Contraseña" style={{ backgroundColor: '#fff' }}
                              data-testid="cuenta-password" />
                            <button type="button" onClick={() => setMostrarPassword(!mostrarPassword)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs">
                              {mostrarPassword ? 'Ocultar' : 'Ver'}
                            </button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Repetir Contraseña *</Label>
                          <Input type={mostrarPassword ? "text" : "password"}
                            value={datosCompartidos.password}
                            disabled className="bg-slate-50 text-slate-500"
                            data-testid="cuenta-password-repeat" />
                          <p className="text-[10px] text-slate-400 mt-0.5">Se usa la misma contraseña</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Cuentas individuales */}
                    <div className="bg-white rounded-lg border border-blue-100 p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">
                          Cuentas individuales ({cuentasIndividuales.length})
                        </p>
                        <button type="button" onClick={agregarCuenta}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                          data-testid="agregar-cuenta-btn">
                          <Plus size={14} />Agregar cuenta
                        </button>
                      </div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {cuentasIndividuales.map((cuenta, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-200">
                            <span className="text-xs font-bold text-slate-500 w-6 text-center">{idx + 1}</span>
                            <div className="flex-1 grid grid-cols-2 gap-2">
                              <Input
                                value={cuenta.razon_social}
                                onChange={(e) => updateCuentaIndividual(idx, 'razon_social', e.target.value)}
                                placeholder="Razón Social"
                                className="h-8 text-sm"
                                style={{ backgroundColor: '#fff' }}
                                data-testid={`cuenta-razon-${idx}`}
                              />
                              <Input
                                value={cuenta.email}
                                onChange={(e) => updateCuentaIndividual(idx, 'email', e.target.value)}
                                placeholder="email@empresa.com"
                                className="h-8 text-sm"
                                style={{ backgroundColor: '#fff' }}
                                data-testid={`cuenta-email-${idx}`}
                              />
                            </div>
                            <button type="button" onClick={() => quitarCuenta(idx)}
                              disabled={cuentasIndividuales.length <= 1}
                              className={`p-1 rounded ${cuentasIndividuales.length <= 1 ? 'text-slate-300' : 'text-red-400 hover:text-red-600 hover:bg-red-50'}`}
                              data-testid={`quitar-cuenta-${idx}`}>
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div>
              <Label>Comentario / Motivo {(decision === 'RECHAZADO' || decision === 'INFO_ADICIONAL' || decision === 'NIVEL_1_DEVUELTO') && <span className="text-red-500">*</span>}</Label>
              <Textarea
                rows={3}
                placeholder={
                  decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO' ? 'Comentario opcional...' :
                  decision === 'RECHAZADO' ? 'Indique el motivo del rechazo...' :
                  decision === 'NIVEL_1_DEVUELTO' ? 'Indique qué debe corregir el primer revisor...' :
                  'Indique qué información adicional necesita...'
                }
                value={decisionComment}
                onChange={(e) => setDecisionComment(e.target.value)}
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDecision}
              disabled={
                deciding || 
                (['RECHAZADO', 'INFO_ADICIONAL', 'NIVEL_1_DEVUELTO'].includes(decision) && !decisionComment.trim()) ||
                ((decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO') && (!limiteDiario || !limiteMensual))
              }
              className={`text-white ${
                decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO' ? 'bg-green-600 hover:bg-green-700' :
                decision === 'RECHAZADO' ? 'bg-red-600 hover:bg-red-700' :
                decision === 'NIVEL_1_DEVUELTO' ? 'bg-amber-600 hover:bg-amber-700' :
                'bg-orange-600 hover:bg-orange-700'
              }`}
            >
              {deciding ? 'Procesando...' : (
                decision === 'APROBADO' ? 'Aprobar Definitivamente' :
                decision === 'NIVEL_1_DEVUELTO' ? 'Devolver' :
                'Confirmar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forward Dialog */}
      <Dialog open={showForwardDialog} onOpenChange={setShowForwardDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Forward className="text-blue-600" />
              Derivar Solicitud
            </DialogTitle>
            <DialogDescription>
              Seleccione a quién desea derivar esta solicitud para revisión.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Asignar a</Label>
              <Select value={forwardTo} onValueChange={setForwardTo}>
                <SelectTrigger style={{ backgroundColor: '#fff' }}>
                  <SelectValue placeholder="Seleccionar usuario" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      <div className="flex items-center gap-2">
                        <span>{u.nombre}</span>
                        <span className="text-xs text-slate-500">({u.rol || u.rol})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Solo puede asignar a usuarios de su mismo nivel o inferior
              </p>
            </div>
            <div>
              <Label>Motivo de la asignación (opcional)</Label>
              <Textarea
                rows={3}
                placeholder="Indique el motivo de la asignación..."
                value={forwardComment}
                onChange={(e) => setForwardComment(e.target.value)}
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForwardDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleForward} disabled={!forwardTo} className="bg-blue-600 hover:bg-blue-700 text-white">
              Asignar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Decision Dialog */}
      <Dialog open={showDocDecisionDialog} onOpenChange={setShowDocDecisionDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {docDecision === 'INVALIDO' ? (
                <AlertTriangle className="text-orange-600" size={20} />
              ) : docDecision === 'RECHAZADO' ? (
                <XCircle className="text-red-600" size={20} />
              ) : (
                <FileText className="text-blue-600" size={20} />
              )}
              {docDecision === 'INVALIDO' ? 'Documento Inválido' : 
               docDecision === 'RECHAZADO' ? 'Rechazar Documento' : 'Revisar Documento'}
            </DialogTitle>
            <DialogDescription>
              {selectedDocument && `Documento: ${selectedDocument.filename}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {docDecision === 'INVALIDO' && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  <strong>Documento Inválido:</strong> El cliente recibirá una notificación para subir 
                  nuevamente este documento con las correcciones indicadas.
                </p>
              </div>
            )}
            {docDecision === 'RECHAZADO' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Rechazo Definitivo:</strong> El documento será rechazado permanentemente. 
                  Use esta opción cuando el documento es completamente inaceptable.
                </p>
              </div>
            )}
            
            <div>
              <Label>Comentario {docDecision === 'INVALIDO' || docDecision === 'RECHAZADO' ? '*' : '(opcional)'}</Label>
              <Textarea
                rows={3}
                placeholder={docDecision === 'INVALIDO' 
                  ? "Indique qué problema tiene el documento y qué se necesita corregir..."
                  : docDecision === 'RECHAZADO'
                  ? "Indique el motivo del rechazo..."
                  : "Indique observaciones si las hay..."}
                value={docDecisionComment}
                onChange={(e) => setDocDecisionComment(e.target.value)}
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocDecisionDialog(false)}>
              Cancelar
            </Button>
            <button
              onClick={handleDocumentDecision}
              disabled={!docDecision || ((docDecision === 'INVALIDO' || docDecision === 'RECHAZADO') && !docDecisionComment.trim())}
              className="px-4 py-2 rounded-md font-medium disabled:opacity-50"
              style={{ 
                backgroundColor: docDecision === 'INVALIDO' ? '#ea580c' : docDecision === 'RECHAZADO' ? '#dc2626' : '#16a34a',
                color: '#ffffff'
              }}
            >
              Confirmar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="text-blue-600" size={20} />
              Modificar Solicitud
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre Representante</Label>
                <Input
                  value={editData.representante_nombre || ''}
                  onChange={(e) => setEditData({ ...editData, representante_nombre: e.target.value })}
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
              <div>
                <Label>Email Representante</Label>
                <Input
                  type="email"
                  value={editData.representante_email || ''}
                  onChange={(e) => setEditData({ ...editData, representante_email: e.target.value })}
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
            </div>
            
            <Card className="p-4 bg-slate-50">
              <h4 className="font-semibold text-slate-700 mb-3">Datos de la Empresa</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Razón Social</Label>
                  <Input
                    value={editData.empresa_data?.razon_social || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      empresa_data: { ...editData.empresa_data, razon_social: e.target.value }
                    })}
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
                <div>
                  <Label>CUIT</Label>
                  <Input
                    value={editData.empresa_data?.cuit || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      empresa_data: { ...editData.empresa_data, cuit: e.target.value }
                    })}
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-blue-50">
              <h4 className="font-semibold text-slate-700 mb-3">Límites</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Límite Diario ($)</Label>
                  <Input
                    type="number"
                    value={editData.limites?.diario || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      limites: { ...editData.limites, diario: parseFloat(e.target.value) || 0 }
                    })}
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
                <div>
                  <Label>Límite Mensual ($)</Label>
                  <Input
                    type="number"
                    value={editData.limites?.mensual || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      limites: { ...editData.limites, mensual: parseFloat(e.target.value) || 0 }
                    })}
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
              </div>
            </Card>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Visor de Documentos */}
      <Dialog open={showDocViewer} onOpenChange={(open) => {
        if (!open) {
          // Liberar URL del blob al cerrar
          if (documentContent?.url && documentContent.url.startsWith('blob:')) {
            URL.revokeObjectURL(documentContent.url);
          }
          setDocumentContent(null);
        }
        setShowDocViewer(open);
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] p-0 overflow-hidden" style={{ backgroundColor: '#1e293b' }}>
          <DialogHeader className="p-4 border-b border-slate-700">
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText size={20} />
              {viewingDocument?.filename || 'Documento'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {viewingDocument?.content_type} • {viewingDocument?.size ? `${Math.round(viewingDocument.size / 1024)} KB` : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="relative" style={{ height: 'calc(90vh - 140px)' }}>
            {loadingDocument ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <RefreshCw size={40} className="animate-spin text-blue-400 mx-auto mb-4" />
                  <p className="text-slate-400">Cargando documento...</p>
                </div>
              </div>
            ) : documentContent ? (
              <>
                {/* PDF Viewer */}
                {documentContent.type?.includes('pdf') && (
                  <iframe
                    src={documentContent.url}
                    className="w-full h-full border-0"
                    title={documentContent.filename}
                  />
                )}
                
                {/* Image Viewer */}
                {documentContent.type?.includes('image') && (
                  <div className="flex items-center justify-center h-full p-4 overflow-auto">
                    <img
                      src={documentContent.url}
                      alt={documentContent.filename}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                )}
                
                {/* Other file types */}
                {!documentContent.type?.includes('pdf') && !documentContent.type?.includes('image') && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center p-8">
                      <FileText size={64} className="text-slate-500 mx-auto mb-4" />
                      <p className="text-slate-300 mb-4">
                        Este tipo de archivo no se puede previsualizar directamente.
                      </p>
                      <Button
                        onClick={() => window.open(documentContent.url, '_blank')}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Download size={16} className="mr-2" />
                        Descargar Archivo
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center p-8">
                  <AlertTriangle size={64} className="text-amber-500 mx-auto mb-4" />
                  <p className="text-slate-300 mb-2">No se pudo cargar el documento</p>
                  <p className="text-slate-500 text-sm mb-4">
                    Es posible que el documento no tenga contenido guardado.
                    <br />
                    Solicite al cliente que vuelva a subir el archivo.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700 flex justify-between items-center">
            <div className="text-slate-400 text-sm">
              {viewingDocument?.uploaded_at && (
                <>Subido: {new Date(viewingDocument.uploaded_at).toLocaleString()}</>
              )}
            </div>
            <div className="flex gap-2">
              {documentContent && (
                <Button
                  variant="outline"
                  onClick={() => window.open(documentContent.url, '_blank')}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <ExternalLink size={16} className="mr-2" />
                  Abrir en nueva pestaña
                </Button>
              )}
              <Button
                onClick={() => setShowDocViewer(false)}
                className="bg-slate-600 hover:bg-slate-500"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
